import React, { useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Link } from 'react-router-dom';

import {
  Box,
  Grid,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText, // ListItemButton,
  ListSubheader,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';

import { ReactComponent as FinderIcon } from '../../assets/img/icon/Finder.svg';
import { ReactComponent as RightIcon } from '../../assets/img/icon/Right.svg';
import { useVehiclesContext } from '../../context/vehicles/vehicles.provider';
import Loader from '../LoaderSplash/Loader.component';

const ORDERED_BY = 'license_plate';

interface VehicleListProps {
  baseUrl?: any;
  setFunction?: (id: any) => void;
  verified?: boolean;
  withPolicy?: boolean;
  showOnAssistance?: boolean;
  plateNumber?: string;
  policyId?: number;
}

const CustomListItem: React.FC<VehicleListProps> = (props) => {
  const { setFunction, baseUrl, children } = props;

  return setFunction ? (
    <ListItem button divider onClick={setFunction}>
      {children}
    </ListItem>
  ) : (
    <ListItem divider component={Link} to={baseUrl}>
      {children}
    </ListItem>
  );
};

const VehicleList: React.FC<VehicleListProps> = (props) => {
  const {
    vehiclesSearched,
    searchVehiclesByQ,
    hasMoreData,
    loading,
    cleanVehicles,
    showOnAssistance,
    plateNumber,
    policyId,
  } = useVehiclesContext();

  const [pageNumber, setPageNumber] = useState(1);
  const { setFunction, baseUrl, verified, withPolicy } = props;
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    searchVehiclesByQ(`&page_number=${pageNumber}`);
    return () => {
      cleanVehicles();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event: any) => {
    setPageNumber(1);
    searchVehiclesByQ(event.target.value, true);
  };

  const classifyingVehicles = useCallback(() => {
    const baseFilter = verified
      ? vehiclesSearched.filter(
          (vehicle: any) =>
            vehicle.policy.status === 'active' &&
            (!!vehicle.assistance_provider?.phone || policyId)
        )
      : vehiclesSearched;

    const firstChar = baseFilter
        .map((vehicle: any) => {
          return vehicle[ORDERED_BY][0];
        })
        .sort(),
      filteredChar = [...new Set<string>(firstChar)];

    const FilteredCharUnique = filteredChar.filter(function (item, pos) {
      return filteredChar.indexOf(item) === pos;
    });

    return FilteredCharUnique.map((filt: string) =>
      baseFilter.filter((vehicle: any) => vehicle[ORDERED_BY][0] === filt)
    );
  }, [policyId, vehiclesSearched, verified]);

  const GetMoreVehicles = () => {
    setPageNumber(pageNumber + 1);
    searchVehiclesByQ(`&page_number=${pageNumber + 1}`);
  };

  return (
    <>
      <Box
        display={
          (loading && vehiclesSearched.length < 1) || !!!plateNumber
            ? 'block'
            : 'none'
        }
      >
        <Loader action="Cargando vehiculos" />
      </Box>
      <Grid container alignItems="center">
        <Grid item xs={11} md={8} lg={7} xl={6}>
          <Grid container>
            <Grid item xs={12}>
              <Box my={3}>
                <TextField
                  name="search"
                  id="search"
                  placeholder="Buscá por N° de patente, Marca o Modelo "
                  variant="outlined"
                  onInput={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FinderIcon />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12}>
            {matches ? (
              <div
                id="scrollableDiv"
                style={{
                  height: 600,
                  overflow: 'auto',
                }}
              >
                <InfiniteScroll
                  dataLength={vehiclesSearched.length} //This is important field to render the next data
                  next={GetMoreVehicles}
                  hasMore={hasMoreData}
                  loader={
                    <Box style={{ width: '100%' }}>
                      <LinearProgress color="primary" />
                    </Box>
                  }
                  endMessage={
                    <Box m={2}>
                      <Typography
                        style={{ textAlign: 'center' }}
                        color="textSecondary"
                      >
                        {vehiclesSearched.length > 0
                          ? 'Ya hemos mostrado todos los resultados'
                          : 'Ningún elemento coincide con el criterio de busqueda'}
                      </Typography>
                    </Box>
                  }
                  scrollableTarget={'scrollableDiv'}
                >
                  {classifyingVehicles().map(
                    (categorie: any[], index: number) => (
                      <List
                        key={index}
                        subheader={
                          <ListSubheader>
                            {categorie[0][ORDERED_BY][0]}
                          </ListSubheader>
                        }
                      >
                        {categorie.map(
                          (vehicle: any) =>
                            (!showOnAssistance ||
                              (showOnAssistance &&
                                vehicle.assistance_provider?.phone)) && (
                              <CustomListItem
                                baseUrl={
                                  withPolicy
                                    ? baseUrl + vehicle.policy.id
                                    : baseUrl + vehicle.id
                                }
                                setFunction={
                                  setFunction
                                    ? () => setFunction(vehicle)
                                    : undefined
                                }
                              >
                                <ListItemText
                                  primary={
                                    <>
                                      <Typography
                                        component="span"
                                        variant="subtitle1"
                                        color="textPrimary"
                                      >
                                        {vehicle[ORDERED_BY]}
                                      </Typography>
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        color="textPrimary"
                                      >
                                        {' / ' +
                                          vehicle.brand +
                                          ' ' +
                                          vehicle.model +
                                          ' ' +
                                          vehicle.year}
                                      </Typography>
                                    </>
                                  }
                                />
                                <ListItemSecondaryAction>
                                  <RightIcon></RightIcon>
                                </ListItemSecondaryAction>
                              </CustomListItem>
                            )
                        )}
                      </List>
                    )
                  )}
                </InfiniteScroll>
              </div>
            ) : (
              <>
                <InfiniteScroll
                  dataLength={vehiclesSearched.length} //This is important field to render the next data
                  next={GetMoreVehicles}
                  hasMore={hasMoreData}
                  loader={
                    <Box style={{ width: '100%' }}>
                      <LinearProgress color="primary" />
                    </Box>
                  }
                  endMessage={
                    <Box m={2}>
                      <Typography
                        style={{ textAlign: 'center' }}
                        color="textSecondary"
                      >
                        {vehiclesSearched.length > 0
                          ? 'Ya hemos mostrado todos los resultados'
                          : 'Ningún elemento coincide con el criterio de busqueda'}
                      </Typography>
                    </Box>
                  }
                  // height={matches ? 600 : null}
                  // onScroll={(event: any) => console.log(event, 'scroll')}
                >
                  {classifyingVehicles().map(
                    (categorie: any[], index: number) => (
                      <List
                        key={index}
                        subheader={
                          <ListSubheader>
                            {categorie[0][ORDERED_BY][0]}
                          </ListSubheader>
                        }
                      >
                        {categorie.map(
                          (vehicle: any) =>
                            (!showOnAssistance ||
                              (showOnAssistance &&
                                vehicle.assistance_provider?.phone)) && (
                              <CustomListItem
                                baseUrl={
                                  withPolicy
                                    ? baseUrl + vehicle.policy.id
                                    : baseUrl + vehicle.id
                                }
                                setFunction={
                                  setFunction
                                    ? () => setFunction(vehicle)
                                    : undefined
                                }
                              >
                                <ListItemText
                                  primary={
                                    <>
                                      <Typography
                                        component="span"
                                        variant="subtitle1"
                                        color="textPrimary"
                                      >
                                        {vehicle[ORDERED_BY]}
                                      </Typography>
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        color="textPrimary"
                                      >
                                        {' / ' +
                                          vehicle.brand +
                                          ' ' +
                                          vehicle.model +
                                          ' ' +
                                          vehicle.year}
                                      </Typography>
                                    </>
                                  }
                                />
                                <ListItemSecondaryAction>
                                  <RightIcon></RightIcon>
                                </ListItemSecondaryAction>
                              </CustomListItem>
                            )
                        )}
                      </List>
                    )
                  )}
                </InfiniteScroll>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default VehicleList;
